(() => {
  if (window.__calmiAutoAnnualSubscriptionLoaded) return;
  window.__calmiAutoAnnualSubscriptionLoaded = true;

  const annualVariantId = 54316740706645;
  const annualTrialPlanId = 691309379925;
  const eligibilityEndpoint =
    'https://us-central1-calmi-680d4.cloudfunctions.net/shopifyTrialEligibility';
  const subscriptionHandles = new Set([
    'calmi-monthly-subscription',
    'calmi-annual-subscription',
    'calmi-premium-monthly-test',
    'calmi-premium-annual-test',
    'calmi-wellness-app',
  ]);
  const routesRoot = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  const deviceStorageKey = 'calmi_trial_device_id';
  const removalCooldownStorageKey =
    'calmi_auto_subscription_removed_until';
  const removalCooldownMs = 15 * 60 * 1000;
  const loader = document.currentScript;
  const customerId = loader?.dataset.customerId || '';
  const customerEmail = loader?.dataset.customerEmail || '';
  const guestCheckout = !customerId;
  let ensurePromise = null;
  let lastKnownSubscriptionPresent = null;
  let inMemoryRemovalCooldownUntil = 0;
  let cooldownTimer = null;
  const runtimeState = window.__calmiAutoAnnualSubscriptionState = {
    status: 'loaded',
    lastError: '',
    lastRunAt: null,
    cooldownUntil: null,
  };

  const cartUrl = (path) => `${routesRoot}${path}`;

  const getDeviceId = () => {
    try {
      let value = localStorage.getItem(deviceStorageKey) || '';
      if (!value) {
        value = (crypto.randomUUID && crypto.randomUUID()) ||
          `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(deviceStorageKey, value);
      }
      return value;
    } catch (_error) {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const deviceId = getDeviceId();

  const getRemovalCooldownUntil = () => {
    let storedUntil = 0;
    try {
      storedUntil = Number(
        localStorage.getItem(removalCooldownStorageKey) || 0,
      );
      if (storedUntil && storedUntil <= Date.now()) {
        localStorage.removeItem(removalCooldownStorageKey);
        storedUntil = 0;
      }
    } catch (_error) {
      // The in-memory value still prevents an immediate re-add on this page.
    }
    if (inMemoryRemovalCooldownUntil <= Date.now()) {
      inMemoryRemovalCooldownUntil = 0;
    }
    return Math.max(storedUntil, inMemoryRemovalCooldownUntil);
  };

  const scheduleAfterCooldown = (until) => {
    if (cooldownTimer) window.clearTimeout(cooldownTimer);
    const delay = Math.max(100, until - Date.now() + 100);
    cooldownTimer = window.setTimeout(() => {
      cooldownTimer = null;
      scheduleEnsure();
    }, delay);
  };

  const markSubscriptionRemoved = () => {
    const until = Date.now() + removalCooldownMs;
    inMemoryRemovalCooldownUntil = until;
    try {
      localStorage.setItem(removalCooldownStorageKey, String(until));
    } catch (_error) {
      // Continue with the in-memory cooldown when storage is unavailable.
    }
    lastKnownSubscriptionPresent = false;
    runtimeState.status = 'removal-cooldown';
    runtimeState.cooldownUntil = new Date(until).toISOString();
    scheduleAfterCooldown(until);
  };

  const isSubscriptionItem = (item) => {
    if (!item?.selling_plan_allocation) return false;
    const properties = item.properties || {};
    const title = String(item.product_title || '').toLowerCase();
    return properties._calmi_subscription === 'true' ||
      subscriptionHandles.has(String(item.handle || '')) ||
      (title.includes('calmi') &&
        (title.includes('monthly') || title.includes('annual')));
  };

  const requestJson = async (path, options = {}) => {
    const response = await originalFetch(cartUrl(path), {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? {'Content-Type': 'application/json'} : {}),
        ...options.headers,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        payload.description || `Cart request failed: ${response.status}`,
      );
    }
    return payload;
  };

  const isTrialEligible = async () => {
    try {
      const response = await originalFetch(eligibilityEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          email: customerEmail,
          deviceId,
          guestCheckout,
        }),
      });
      if (!response.ok) return false;
      const result = await response.json();
      return result.eligible !== false;
    } catch (error) {
      console.error('Calmi automatic trial eligibility failed', error);
      return false;
    }
  };

  const notifyCartUpdated = async () => {
    const cart = await requestJson('cart.js');
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'calmi-auto-annual-subscription',
        data: {
          source: 'calmi-auto-annual-subscription',
          itemCount: cart.item_count,
        },
      },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {cart, openMiniCart: true},
    }));
  };

  const ensureAnnualTrial = async () => {
    runtimeState.status = 'checking';
    runtimeState.lastRunAt = new Date().toISOString();
    let cart = await requestJson('cart.js');
    let subscriptionItems = (cart.items || []).filter(isSubscriptionItem);
    const regularItems = (cart.items || []).filter((item) =>
      !isSubscriptionItem(item));
    if (!regularItems.length) {
      runtimeState.status = 'no-regular-items';
      return;
    }

    if (subscriptionItems.length) {
      lastKnownSubscriptionPresent = true;
      runtimeState.cooldownUntil = null;
      const updates = Object.fromEntries(
        subscriptionItems
          .filter((item) => Number(item.quantity) !== 1)
          .map((item) => [item.key, 1]),
      );
      if (Object.keys(updates).length) {
        await requestJson('cart/update.js', {
          method: 'POST',
          body: JSON.stringify({updates}),
        });
        await notifyCartUpdated();
      }
      runtimeState.status = 'subscription-present';
      return;
    }

    if (lastKnownSubscriptionPresent === true) {
      markSubscriptionRemoved();
      return;
    }
    lastKnownSubscriptionPresent = false;

    const removalCooldownUntil = getRemovalCooldownUntil();
    if (removalCooldownUntil > Date.now()) {
      runtimeState.status = 'removal-cooldown';
      runtimeState.cooldownUntil =
        new Date(removalCooldownUntil).toISOString();
      scheduleAfterCooldown(removalCooldownUntil);
      return;
    }
    runtimeState.cooldownUntil = null;

    if (!(await isTrialEligible())) {
      runtimeState.status = 'not-eligible';
      return;
    }

    // Re-read the cart after the eligibility request to avoid adding a second
    // subscription if another cart action completed in the meantime.
    cart = await requestJson('cart.js');
    subscriptionItems = (cart.items || []).filter(isSubscriptionItem);
    if (subscriptionItems.length ||
        !(cart.items || []).some((item) => !isSubscriptionItem(item))) {
      runtimeState.status = subscriptionItems.length ?
        'subscription-present' : 'no-regular-items';
      return;
    }

    await requestJson('cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        id: annualVariantId,
        quantity: 1,
        selling_plan: annualTrialPlanId,
        properties: {
          _calmi_subscription: 'true',
          _calmi_plan_type: 'trial',
          _calmi_auto_added: 'true',
          _calmi_device_id: deviceId,
          _calmi_guest_checkout: String(guestCheckout),
        },
      }),
    });
    await requestJson('cart/update.js', {
      method: 'POST',
      body: JSON.stringify({
        attributes: {
          calmi_device_id: deviceId,
          calmi_guest_checkout: String(guestCheckout),
        },
      }),
    });
    await notifyCartUpdated();
    lastKnownSubscriptionPresent = true;
    runtimeState.status = 'added';
  };

  const scheduleEnsure = () => {
    if (ensurePromise) return ensurePromise;
    ensurePromise = ensureAnnualTrial()
      .catch((error) => {
        runtimeState.status = 'error';
        runtimeState.lastError = String(error?.message || error);
        console.error('Calmi automatic annual trial failed', error);
      })
      .finally(() => {
        ensurePromise = null;
      });
    return ensurePromise;
  };

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (!response.ok) return response;

    const requestUrl = typeof args[0] === 'string' ?
      args[0] : String(args[0]?.url || '');
    let pathname = requestUrl;
    try {
      pathname = new URL(requestUrl, window.location.origin).pathname;
    } catch (_error) {
      // Keep the original path when it cannot be parsed as a URL.
    }
    if (/\/cart\/add(?:\.js)?\/?$/.test(pathname)) {
      queueMicrotask(scheduleEnsure);
    }
    return response;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!String(form.action || '').includes('/cart/add')) return;
    window.setTimeout(scheduleEnsure, 350);
    window.setTimeout(scheduleEnsure, 1000);
  }, true);

  document.addEventListener('click', (event) => {
    const control = event.target instanceof Element ?
      event.target.closest('a, button') : null;
    if (!control) return;

    const href = String(control.getAttribute('href') || '');
    const label = `${control.getAttribute('aria-label') || ''} ` +
      `${control.getAttribute('title') || ''} ${control.textContent || ''}`;
    const row = control.closest(
      'cart-drawer-item, cart-item, [data-cart-item], .cart-item, tr, li',
    );
    const rowText = String(row?.textContent || '').toLowerCase();
    const isRemoveControl =
      (/\/cart\/change/.test(href) && /quantity=0/.test(href)) ||
      /\b(remove|delete)\b/i.test(label);
    const isCalmiSubscription =
      href.includes(String(annualVariantId)) ||
      (rowText.includes('calmi') &&
        (rowText.includes('monthly') || rowText.includes('annual')));

    if (isRemoveControl && isCalmiSubscription) {
      markSubscriptionRemoved();
    }
  }, true);

  document.addEventListener('cart:update', (event) => {
    const sourceId = event.detail?.sourceId || '';
    const source = event.detail?.data?.source || '';
    if (sourceId === 'calmi-auto-annual-subscription' ||
        source === 'calmi-auto-annual-subscription') return;
    queueMicrotask(scheduleEnsure);
  });

  window.addEventListener('pageshow', scheduleEnsure);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleEnsure();
  });

  scheduleEnsure();
  window.setTimeout(scheduleEnsure, 500);
  window.setTimeout(scheduleEnsure, 1500);
  window.setTimeout(scheduleEnsure, 3000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEnsure, {once: true});
  }
})();
